class YotpoSubscriptionWidget extends HTMLElement {
  constructor() {
    super();
    this.hideCookieName = 'hide_subscription_widget';
    this.subscribeOption = null;
    this.oneTimeOption = null;
    this.sellingPlanInput = null;
    this.planButtons = [];
    this.isInitialized = false;
  }

  connectedCallback() {
    // Check manual hide cookie to prevent visual jumping
    if (this.shouldHideWidget()) {
      this.style.display = 'none';
      // Ensure one-time option is selected when widget is hidden
      requestAnimationFrame(() => {
        this.selectOneTimeOption();
      });
      return;
    }

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      this.init();
    });
  }

  init() {
    if (this.isInitialized) return;
    
    this.cacheElements();
    this.bindEvents();
    
    // Check for promo and set default selection
    this.setInitialSelection();
    this.checkActiveOption();
    
    this.isInitialized = true;
  }

  setInitialSelection() {
    // If there's an active promo, default to one-time purchase
    if (this.hasActivePromo()) {
      this.selectOneTimeOption();
    }
  }

  shouldHideWidget() {
    // Only check manual hide cookie (not promo status)
    return document.cookie
      .split('; ')
      .find(row => row.startsWith(`${this.hideCookieName}=`))
      ?.split('=')[1] === 'true';
  }

  hasActivePromo() {
      // If there's an active promo, default to one-time purchase
      let productInfo = document.querySelector('product-info');
    // Check if promo tracker is available and has active promo
    if (typeof window.promoTracker !== 'undefined' && window.promoTracker && productInfo?.hasAttribute('data-default-otp')) {
      try {
        const promoStatus = window.promoTracker.getPromoStatus();
        return promoStatus.hasPromo;
      } catch (error) {
        console.warn('YotpoSubscriptionWidget: Error checking promo status', error);
        return false;
      }
    }
    return false;
  }

  selectOneTimeOption() {
    const oneTimeOption = this.querySelector('.purchase_option_one-time');
    const sellingPlanInput = document.querySelector('.selling_plan_input');
    
    if (oneTimeOption) {
      oneTimeOption.checked = true;
      if (sellingPlanInput) {
        sellingPlanInput.value = '';
      }
      
      // Log when one-time is selected due to promo
      if (this.hasActivePromo()) {
        console.log('[YotpoSubscriptionWidget] One-time purchase selected - active promo detected');
      }
      
      // Update content to reflect one-time selection
      this.updateWidgetContent();
    }
  }

  cacheElements() {
    this.subscribeOption = this.querySelector('.purchase_option_subscribe');
    this.oneTimeOption = this.querySelector('.purchase_option_one-time');
    this.sellingPlanInput = document.querySelector('.selling_plan_input');
    this.planButtons = Array.from(this.querySelectorAll('.subscription__plan-button'));
  }

  bindEvents() {
    if (!this.subscribeOption || !this.oneTimeOption) return;

    // Plan button handlers
    this.planButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handlePlanClick(e));
    });

    // Purchase option handlers
    this.subscribeOption.addEventListener('change', () => this.checkActiveOption());
    this.oneTimeOption.addEventListener('change', () => this.checkActiveOption());

    // Global variant change listener
    window.addEventListener('variant:change', () => this.updateWidgetContent());
    window.addEventListener('load', () => this.checkActiveOption());

    // Listen for promo events
    document.addEventListener('promoApplied', () => this.handlePromoChange());
    
    // Listen for sale prices config changes
    document.addEventListener('salePrices:updated', () => {
      // Re-apply OTP pricing when sale prices config changes
      const variantData = this.getSelectedVariantData();
      if (variantData) {
        this.updateOneTimePricing(variantData);
      }
    });
    
    // Periodic check for promo tracker availability (in case it loads after this widget)
    this.checkPromoTrackerPeriodically();
  }

  handlePlanClick(e) {
    const button = e.target;
    
    // Update active state
    this.planButtons.forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');
    
    // Select subscription option
    this.subscribeOption.checked = true;
    
    // Update selling plan
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = button.dataset.planId;
    }
    
    this.checkActiveOption();
  }

  checkActiveOption() {
    if (!this.subscribeOption || !this.oneTimeOption || !this.sellingPlanInput) return;

    if (this.subscribeOption.checked) {
      const activeButton = this.querySelector('.subscription__plan-button.active');
      if (activeButton) {
        this.sellingPlanInput.value = activeButton.dataset.planId;
      }
    } else if (this.oneTimeOption.checked) {
      this.sellingPlanInput.value = '';
    }

    this.updateWidgetContent();
  }

  updateWidgetContent() {
    try {
      const variantData = this.getSelectedVariantData();
      if (!variantData) return;

      const activePlanPosition = this.getActivePlanPosition();
      
      this.updateOneTimePricing(variantData);
      this.updateSubscriptionPricing(variantData, activePlanPosition);
      
      // Re-apply sale prices if enabled (to ensure Liquid-rendered prices are preserved)
      if (window.salePricesConfig && window.salePricesConfig.enabled) {
        // Force re-render to ensure original__price is shown
        requestAnimationFrame(() => {
          this.updateSubscriptionPricing(variantData, activePlanPosition);
        });
      }
    } catch (error) {
      console.warn('YotpoSubscriptionWidget: Error updating content', error);
    }
  }

  getSelectedVariantData() {
    const variantElement = document.querySelector('.product [data-selected-variant]');
    return variantElement ? JSON.parse(variantElement.innerHTML) : null;
  }

  getActivePlanPosition() {
    const activeButton = this.querySelector('.subscription__plan-button.active');
    return activeButton ? Number(activeButton.dataset.optionPosition) : 0;
  }

  updateOneTimePricing(variant) {
    const originalPriceEl = this.querySelector('.one-time-price__wrapper .original__price');
    const salePriceEl = this.querySelector('.one-time-price__wrapper .sale__price');
    
    if (!originalPriceEl || !salePriceEl) return;

    // Check if sale prices module is enabled
    const salePricesEnabled = window.salePricesConfig && window.salePricesConfig.enabled;
    
    if (salePricesEnabled) {
      // Apply sale price logic for OTP
      this.applyOTPSalePrice(variant, originalPriceEl, salePriceEl);
    } else {
      // Use standard variant pricing (no sale price override)
      // Clean up sale price flags and styling
      delete salePriceEl.dataset.salePriceProcessed;
      const priceWrapper = salePriceEl.closest('.one-time-price__wrapper');
      if (priceWrapper) {
        priceWrapper.classList.remove('otp-price--sale-applied');
      }
      
      const hasDiscount = variant.price < variant.compare_at_price;
      originalPriceEl.style.display = hasDiscount ? 'block' : 'none';
      
      salePriceEl.innerHTML = Shopify.formatMoney(variant.price);
      originalPriceEl.innerHTML = Shopify.formatMoney(variant.compare_at_price);
    }
  }

  /**
   * Apply sale price to one-time purchase option
   * Calculates sale price based on salePrices module configuration
   */
  applyOTPSalePrice(variant, originalPriceEl, salePriceEl) {
    // Get discount percent from sale prices module (default 40%)
    const discountPercent = window.salePricesConfig.discountPercent || 40;
    
    // Determine base price: use compare_at_price if available, otherwise use regular price
    const basePrice = variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
    
    // Calculate sale price
    const discountMultiplier = 1 - (discountPercent / 100);
    const salePriceCents = Math.round(basePrice * discountMultiplier);
    
    // Format prices
    const formattedSalePrice = Shopify.formatMoney(salePriceCents);
    const formattedOriginalPrice = Shopify.formatMoney(basePrice);
    
    // Update sale price element
    salePriceEl.innerHTML = formattedSalePrice;
    
    // Always show original price when sale prices are enabled
    originalPriceEl.style.display = 'block';
    originalPriceEl.innerHTML = formattedOriginalPrice;
    
    // Mark as processed to prevent sale-prices.js from double-processing
    salePriceEl.dataset.salePriceProcessed = 'true';
    
    // Add sale styling class
    const priceWrapper = salePriceEl.closest('.one-time-price__wrapper');
    if (priceWrapper) {
      priceWrapper.classList.add('otp-price--sale-applied');
    }
  }

  updateSubscriptionPricing(variant, planPosition) {
    const originalPriceEl = this.querySelector('.subscription-price__wrapper .original__price');
    const salePriceEl = this.querySelector('.subscription-price__wrapper .sale__price');
    
    if (!originalPriceEl || !salePriceEl || !variant.selling_plan_allocations) return;

    // If prices are Liquid-rendered with sale prices, preserve them
    if (originalPriceEl.dataset.liquidRendered === 'true' || salePriceEl.dataset.liquidRendered === 'true') {
      // Ensure original__price is visible if it exists
      if (originalPriceEl.textContent.trim()) {
        originalPriceEl.style.display = 'block';
      }
      return; // Don't overwrite Liquid-rendered prices
    }

    const allocation = variant.selling_plan_allocations[planPosition];
    if (!allocation) return;

    // Check if sale prices module is enabled
    const salePricesEnabled = window.salePricesConfig && window.salePricesConfig.enabled;
    
    if (salePricesEnabled) {
      // Apply sale price logic for subscription pricing
      this.applySubscriptionSalePrice(allocation, originalPriceEl, salePriceEl);
    } else {
      // Use standard subscription pricing
      salePriceEl.innerHTML = Shopify.formatMoney(allocation.price);
      const hasDiscount = allocation.price < allocation.compare_at_price;
      originalPriceEl.style.display = hasDiscount ? 'block' : 'none';
      originalPriceEl.innerHTML = Shopify.formatMoney(allocation.compare_at_price);
    }
  }

  /**
   * Apply sale price to subscription pricing
   * Calculates sale price based on salePrices module configuration
   */
  applySubscriptionSalePrice(allocation, originalPriceEl, salePriceEl) {
    // Get discount percent from sale prices module (default 40%)
    const discountPercent = window.salePricesConfig.discountPercent || 40;
    
    // Determine base price: use compare_at_price if available, otherwise calculate from subscription price
    let basePrice = allocation.compare_at_price;
    if (!basePrice || basePrice === 0) {
      // Calculate compare_at_price from subscription price using reverse discount
      const discountMultiplier = 1 - (discountPercent / 100);
      basePrice = Math.round(allocation.price / discountMultiplier);
    }
    
    // Calculate sale price from compare_at_price
    const discountMultiplier = 1 - (discountPercent / 100);
    const salePriceCents = Math.round(basePrice * discountMultiplier);
    
    // Format prices
    const formattedSalePrice = Shopify.formatMoney(salePriceCents);
    const formattedOriginalPrice = Shopify.formatMoney(basePrice);
    
    // Update prices
    salePriceEl.innerHTML = formattedSalePrice;
    
    // Always show original price when sale prices are enabled
    originalPriceEl.style.display = 'block';
    originalPriceEl.innerHTML = formattedOriginalPrice;
    
    // Mark as processed to prevent sale-prices.js from modifying
    salePriceEl.dataset.salePriceProcessed = 'true';
    originalPriceEl.dataset.salePriceProcessed = 'true';
  }

  // Public method to hide widget via cookie
  hideWidget() {
    document.cookie = `${this.hideCookieName}=true; path=/; max-age=31536000`; // 1 year
    this.style.display = 'none';
    
    // Select one-time option if available
    if (this.oneTimeOption) {
      this.oneTimeOption.checked = true;
      this.checkActiveOption();
    }
  }

  // Public method to show widget
  showWidget() {
    document.cookie = `${this.hideCookieName}=false; path=/; max-age=31536000`;
    this.style.display = 'block';
  }

  handlePromoChange() {
    // When promo status changes, update selection but keep widget visible
    if (this.hasActivePromo()) {
      this.selectOneTimeOption();
    }
  }

  checkPromoTrackerPeriodically() {
    // Check for promo tracker availability every 500ms for up to 5 seconds
    let attempts = 0;
    const maxAttempts = 10;
    
    const checkInterval = setInterval(() => {
      attempts++;
      
      if (this.hasActivePromo()) {
        // Promo tracker became available and has active promo - select one-time
        this.handlePromoChange();
        clearInterval(checkInterval);
      } else if (attempts >= maxAttempts) {
        // Stop checking after max attempts
        clearInterval(checkInterval);
      }
    }, 500);
  }
}

// Register the custom element
if (!customElements.get('yotpo-subscription-widget')) {
  customElements.define('yotpo-subscription-widget', YotpoSubscriptionWidget);
}
